import React from "react";
import { Card, CardContent } from "../components/ui/card";

const PrivacyPolicy = () => {
  return (
    <div className="min-h-screen bg-gray-50 py-12">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="bg-white rounded-lg shadow-lg p-8 md:p-12">
          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-8">
            Privacy Policy
          </h1>

          <div className="space-y-8 text-gray-700 leading-relaxed">
            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">
                1. Introduction
              </h2>
              <p>
                At Trademark Upgrades, we are committed to protecting your
                privacy and ensuring the security of your personal information.
                This Privacy Policy explains how we collect, use, disclose, and
                safeguard your information when you visit our website or use our
                services.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">
                2. Information We Collect
              </h2>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">
                2.1 Personal Information
              </h3>
              <p className="mb-4">
                We may collect personal information that you provide directly to
                us, including:
              </p>
              <ul className="list-disc pl-6 space-y-2">
                <li>
                  Name and contact information (email address, phone number,
                  mailing address)
                </li>
                <li>
                  Business information (company name, industry, business type)
                </li>
                <li>
                  Trademark information (desired marks, descriptions, classes of
                  goods/services)
                </li>
                <li>
                  Payment information (processed securely through third-party
                  payment processors)
                </li>
                <li>Communication preferences and correspondence with us</li>
              </ul>

              <h3 className="text-xl font-semibold text-gray-900 mb-3 mt-6">
                2.2 Automatically Collected Information
              </h3>
              <p className="mb-4">
                When you visit our website, we may automatically collect certain
                information, including:
              </p>
              <ul className="list-disc pl-6 space-y-2">
                <li>IP address and location data</li>
                <li>Browser type and version</li>
                <li>Device information</li>
                <li>Pages viewed and time spent on pages</li>
                <li>Referring website addresses</li>
                <li>Clickstream data</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">
                3. How We Use Your Information
              </h2>
              <p className="mb-4">
                We use the information we collect for various purposes,
                including:
              </p>
              <ul className="list-disc pl-6 space-y-2">
                <li>
                  Providing trademark registration and intellectual property
                  services
                </li>
                <li>Processing your applications and managing your account</li>
                <li>
                  Communicating with you about your services and applications
                </li>
                <li>
                  Responding to your inquiries and providing customer support
                </li>
                <li>
                  Sending you updates, newsletters, and marketing communications
                  (with your consent)
                </li>
                <li>Improving our website, services, and user experience</li>
                <li>
                  Complying with legal obligations and protecting our rights
                </li>
                <li>Preventing fraud and ensuring security</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">
                4. Information Sharing and Disclosure
              </h2>
              <p className="mb-4">
                We may share your information in the following circumstances:
              </p>
              <ul className="list-disc pl-6 space-y-2">
                <li>
                  <strong>With Government Agencies:</strong> We share necessary
                  information with the USPTO and other relevant government
                  agencies to process your trademark applications.
                </li>
                <li>
                  <strong>Service Providers:</strong> We may share information
                  with third-party service providers who assist us in operating
                  our business (e.g., payment processors, IT services).
                </li>
                <li>
                  <strong>Legal Requirements:</strong> We may disclose
                  information when required by law, court order, or legal
                  process.
                </li>
                <li>
                  <strong>Business Transfers:</strong> In connection with any
                  merger, sale, or acquisition, your information may be
                  transferred to the acquiring entity.
                </li>
                <li>
                  <strong>With Your Consent:</strong> We may share information
                  for other purposes with your explicit consent.
                </li>
              </ul>
              <p className="mt-4">
                We do not sell, rent, or trade your personal information to
                third parties for their marketing purposes.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">
                5. Data Security
              </h2>
              <p>
                We implement appropriate technical and organizational security
                measures to protect your personal information against
                unauthorized access, alteration, disclosure, or destruction.
                These measures include encryption, secure servers, access
                controls, and regular security assessments. However, no method
                of transmission over the internet or electronic storage is 100%
                secure, and we cannot guarantee absolute security.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">
                6. Data Retention
              </h2>
              <p>
                We retain your personal information for as long as necessary to
                fulfill the purposes outlined in this Privacy Policy, comply
                with legal obligations, resolve disputes, and enforce our
                agreements. Trademark application records are typically retained
                in accordance with professional and legal requirements.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">
                7. Your Rights and Choices
              </h2>
              <p className="mb-4">
                You have certain rights regarding your personal information:
              </p>
              <ul className="list-disc pl-6 space-y-2">
                <li>
                  <strong>Access:</strong> You can request access to the
                  personal information we hold about you.
                </li>
                <li>
                  <strong>Correction:</strong> You can request that we correct
                  inaccurate or incomplete information.
                </li>
                <li>
                  <strong>Deletion:</strong> You can request deletion of your
                  personal information, subject to legal and contractual
                  obligations.
                </li>
                <li>
                  <strong>Opt-Out:</strong> You can opt out of marketing
                  communications at any time by following the unsubscribe
                  instructions in our emails.
                </li>
                <li>
                  <strong>Data Portability:</strong> You can request a copy of
                  your data in a portable format.
                </li>
              </ul>
              <p className="mt-4">
                To exercise these rights, please contact us using the
                information provided below.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">
                8. Cookies and Tracking Technologies
              </h2>
              <p>
                We use cookies and similar tracking technologies to enhance your
                experience on our website, analyze usage patterns, and deliver
                personalized content. You can control cookie preferences through
                your browser settings. Please note that disabling cookies may
                affect the functionality of our website.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">
                9. Third-Party Links
              </h2>
              <p>
                Our website may contain links to third-party websites. We are
                not responsible for the privacy practices of these external
                sites. We encourage you to review the privacy policies of any
                third-party sites you visit.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">
                10. Children's Privacy
              </h2>
              <p>
                Our services are not intended for individuals under the age of
                18. We do not knowingly collect personal information from
                children. If we become aware that we have collected information
                from a child, we will take steps to delete such information.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">
                11. Changes to This Privacy Policy
              </h2>
              <p>
                We may update this Privacy Policy from time to time to reflect
                changes in our practices or for legal, operational, or
                regulatory reasons. We will notify you of any material changes
                by posting the updated policy on our website with a new "Last
                Updated" date. Your continued use of our services after such
                changes constitutes acceptance of the updated policy.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">
                12. Contact Us
              </h2>
              <p className="mb-4">
                If you have any questions, concerns, or requests regarding this
                Privacy Policy or our data practices, please contact us:
              </p>
              <Card className="bg-orange-50 border-orange-200">
                <CardContent className="pt-6">
                  <p className="font-semibold text-gray-900 mb-2">
                    Trademark Upgrades
                  </p>
                  <p className="text-gray-700">
                    Email: privacy@trademarkupgrades.com
                  </p>
                  <p className="text-gray-700">Phone: +1 (605) 269 5389</p>
                  <p className="text-gray-700">
                    Address: 11811 North Freeway Suite 222, Houston, TX 77060
                  </p>
                </CardContent>
              </Card>
            </section>

            <section className="border-t pt-8 mt-8">
              <p className="text-sm text-gray-600">
                This Privacy Policy is effective as of the date stated above and
                applies to all users of our website and services. By using our
                services, you acknowledge that you have read and understood this
                Privacy Policy.
              </p>
            </section>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicy;
